// src/components/RecipeBuilder.jsx
import React, { useState, useEffect } from "react";

// ✅ Prebuilt Recipes
const prebuiltRecipes = [
  {
    name: "Scrambled Eggs",
    image: "https://source.unsplash.com/300x200/?scrambled-eggs",
    ingredients: [
      { name: "Egg", calories: 70, protein: 6, fat: 5, carbs: 1 },
      { name: "Butter", calories: 100, protein: 0, fat: 11, carbs: 0 }
    ]
  },
  {
    name: "Chicken Salad",
    image: "https://source.unsplash.com/300x200/?chicken-salad",
    ingredients: [
      { name: "Chicken Breast", calories: 165, protein: 31, fat: 4, carbs: 0 },
      { name: "Lettuce", calories: 15, protein: 1, fat: 0, carbs: 2 }
    ]
  },
  {
    name: "Oatmeal Bowl",
    image: "https://source.unsplash.com/300x200/?oatmeal",
    ingredients: [
      { name: "Oats", calories: 150, protein: 5, fat: 3, carbs: 27 },
      { name: "Milk", calories: 100, protein: 8, fat: 5, carbs: 12 }
    ]
  }
];

const MEAL_TYPES = ["Breakfast", "Lunch", "Dinner", "Snack"];

function RecipeBuilder() {
  const [recipeName, setRecipeName] = useState("");
  const [mealType, setMealType] = useState("Breakfast");
  const [ingredient, setIngredient] = useState({
    name: "",
    calories: "",
    protein: "",
    fat: "",
    carbs: ""
  });
  const [ingredients, setIngredients] = useState([]);
  const [savedRecipes, setSavedRecipes] = useState([]);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("savedRecipes")) || [];
    setSavedRecipes(saved);
  }, []);

  const addIngredient = () => {
    if (!ingredient.name) return;

    const cleanIngredient = {
      name: ingredient.name,
      calories: parseFloat(ingredient.calories) || 0,
      protein: parseFloat(ingredient.protein) || 0,
      fat: parseFloat(ingredient.fat) || 0,
      carbs: parseFloat(ingredient.carbs) || 0
    };

    setIngredients([...ingredients, cleanIngredient]);

    setIngredient({
      name: "",
      calories: "",
      protein: "",
      fat: "",
      carbs: ""
    });
  };

  const getTotal = (key) => {
    return ingredients.reduce((total, item) => {
      const value = parseFloat(item[key]);
      return total + (isNaN(value) ? 0 : value);
    }, 0).toFixed(1);
  };

  const saveRecipe = () => {
    if (!recipeName || ingredients.length === 0) return alert("Please enter a recipe name and at least one ingredient.");

    const newRecipe = {
      name: recipeName,
      mealType,
      ingredients,
      totals: {
        calories: getTotal("calories"),
        protein: getTotal("protein"),
        fat: getTotal("fat"),
        carbs: getTotal("carbs")
      }
    };

    const updated = [...savedRecipes, newRecipe];
    setSavedRecipes(updated);
    localStorage.setItem("savedRecipes", JSON.stringify(updated));

    setRecipeName("");
    setIngredients([]);
    alert("Recipe saved successfully!");
  };

  return (
    <div className="recipe-builder-container">
      <h2>Create Your Recipe</h2>

      {/* ✨ Prebuilt Recipes */}
      <h4>✨ Try a Prebuilt Recipe</h4>
      <div className="prebuilt-grid">
        {prebuiltRecipes.map((r, idx) => (
          <div
            key={idx}
            className="prebuilt-card"
            onClick={() => {
              setRecipeName(r.name);
              setIngredients(r.ingredients);
            }}
          >
            <img src={r.image} alt={r.name} />
            <p><strong>{r.name}</strong></p>
          </div>
        ))}
      </div>

      {/* 🧾 Recipe Name */}
      <input
        type="text"
        placeholder="Recipe Name"
        value={recipeName}
        onChange={(e) => setRecipeName(e.target.value)}
      />

      {/* 🍽️ Meal Type Tabs */}
      <div className="meal-tabs">
        {MEAL_TYPES.map((type) => (
          <button
            key={type}
            className={mealType === type ? "active" : ""}
            onClick={() => setMealType(type)}
          >
            {type}
          </button>
        ))}
      </div>

      {/* 🥕 Ingredient Inputs */}
      <h4>Add Ingredient</h4>
      <input type="text" placeholder="Name" value={ingredient.name} onChange={(e) => setIngredient({ ...ingredient, name: e.target.value })} />
      <input type="number" placeholder="Calories" value={ingredient.calories} onChange={(e) => setIngredient({ ...ingredient, calories: e.target.value })} />
      <input type="number" placeholder="Protein (g)" value={ingredient.protein} onChange={(e) => setIngredient({ ...ingredient, protein: e.target.value })} />
      <input type="number" placeholder="Fat (g)" value={ingredient.fat} onChange={(e) => setIngredient({ ...ingredient, fat: e.target.value })} />
      <input type="number" placeholder="Carbs (g)" value={ingredient.carbs} onChange={(e) => setIngredient({ ...ingredient, carbs: e.target.value })} />
      <button onClick={addIngredient}>➕ Add Ingredient</button>

      <hr />

      {/* 📋 Ingredient List */}
      {ingredients.length > 0 && (
        <>
          <h3>{recipeName ? `${recipeName}'s Ingredients:` : "Your Recipe"}</h3>
          <ul>
            {ingredients.map((item, index) => (
              <li key={index}>
                {item.name} — {item.calories} kcal, {item.protein}g protein, {item.fat}g fat, {item.carbs}g carbs
              </li>
            ))}
          </ul>

          {/* 📊 Totals */}
          <div style={{ marginTop: "10px" }}>
            <strong>Total for {recipeName || "this recipe"}:</strong><br />
            Calories: {getTotal("calories")} kcal<br />
            Protein: {getTotal("protein")} g<br />
            Fat: {getTotal("fat")} g<br />
            Carbs: {getTotal("carbs")} g
          </div>
          <button onClick={saveRecipe}>💾 Save Recipe</button>
        </>
      )}

      {/* 📚 Saved Recipes */}
      <hr />
      <h3>📚 Saved Recipes</h3>
      {MEAL_TYPES.map((type) => (
        <div key={type}>
          <h4>{type}</h4>
          {savedRecipes.filter(r => r.mealType === type).length === 0 ? (
            <p>No recipes yet.</p>
          ) : (
            <ul>
              {savedRecipes.filter(r => r.mealType === type).map((r, i) => (
                <li key={i}>
                  <strong>{r.name}</strong> — {r.totals.calories} kcal, {r.totals.protein}g protein
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

export default RecipeBuilder;
